import {computed, decorate, observable} from 'mobx';
import moment from 'moment';
import {BaseModel} from './base';
import {displayableProperty} from './displayableProperty';
import i18n from '../i18n/i18n';
import {displayablePropertyParam} from './displayablePropertyParam';
import {transformation} from '../utils/transformations';
import {InspectionTask} from './inspectionTask';
import {datetimeFormat, durationFormat} from '../config/moment';
import {sortAlphabetically, sortChronologically, sortNumerically} from '../components/shared/tables/sorters';

export class InspectionTaskResult extends BaseModel {
  displayableProperties = [
    displayableProperty({
      key: 'createdAt',
      title: i18n.t('inspectionTaskResult.model.attributes.createdAt'),
      params: [
        displayablePropertyParam({
          path: 'createdAt', transform: transformation.datetime({format: datetimeFormat}),
        }),
      ],
      template: '{value}',
      sorter: (a, b) => sortChronologically(a.createdAt, b.createdAt),
    }),
    displayableProperty({
      key: 'createdBy',
      title: i18n.t('inspectionTaskResult.model.attributes.createdBy'),
      params: [displayablePropertyParam({path: 'createdByUser.fullName', transform: transformation.none})],
      template: '{value}',
      sorter: (a, b) => sortChronologically(a.createdBy.fullName, b.createdBy.fullName),
    }),
    displayableProperty({
      key: 'dueDate',
      title: i18n.t('inspectionTaskResult.model.attributes.dueDate'),
      params: [
        displayablePropertyParam({
          path: 'dueDate', transform: transformation.datetime({format: datetimeFormat}),
        }),
      ],
      template: '{value}',
      sorter: (a, b) => sortChronologically(a.dueDate, b.dueDate),
    }),
    displayableProperty({
      key: 'overdueDifference',
      title: i18n.t('inspectionTaskResult.model.attributes.overdueDifference'),
      params: [
        displayablePropertyParam({
          path: 'overdueDifference', transform: transformation.durationOrDash({format: durationFormat}),
        }),
      ],
      template: '{value}',
      sorter: (a, b) => sortNumerically(a.overdueDifference, b.overdueDifference),
    }),
    displayableProperty({
      key: 'overdueDifferenceAbsolute',
      title: i18n.t('inspectionTaskResult.model.attributes.overdueDifferenceAbsolute'),
      params: [
        displayablePropertyParam({
          path: 'overdueDifferenceAbsolute', transform: transformation.durationOrDash({format: durationFormat}),
        }),
      ],
      template: '{value}',
      sorter: (a, b) => sortNumerically(a.overdueDifferenceAbsolute, b.overdueDifferenceAbsolute),
    }),
    displayableProperty({
      key: 'value',
      title: i18n.t('inspectionTaskResult.model.attributes.value'),
      params: [displayablePropertyParam({path: 'value', transform: transformation.none})],
      template: '{value}',
      sorter: (a, b) => sortAlphabetically(a.value, b.value),
    }),
    displayableProperty({
      key: 'valuation',
      title: i18n.t('inspectionTaskResult.model.attributes.valuation'),
      params: [displayablePropertyParam({path: 'valuation', transform: transformation.none})],
      template: '{value}',
      sorter: (a, b) => sortAlphabetically(a.valuation, b.valuation),
    }),
    displayableProperty({
      key: 'remark',
      title: i18n.t('inspectionTaskResult.model.attributes.remark'),
      params: [displayablePropertyParam({path: 'remark', transform: transformation.none})],
      template: '{value}',
      sorter: (a, b) => sortAlphabetically(a.remark, b.remark),
    }),
    displayableProperty({
      key: 'overruledValue',
      title: i18n.t('inspectionTaskResult.model.attributes.overruledValue'),
      params: [displayablePropertyParam({path: 'overruledValue', transform: transformation.none})],
      template: '{value}',
      sorter: (a, b) => sortAlphabetically(a.overruledValue, b.overruledValue),
    }),
    displayableProperty({
      key: 'overruledValuation',
      title: i18n.t('inspectionTaskResult.model.attributes.overruledValuation'),
      params: [displayablePropertyParam({path: 'overruledValuation', transform: transformation.none})],
      template: '{value}',
      sorter: (a, b) => sortAlphabetically(a.overruledValuation, b.overruledValuation),
    }),
    displayableProperty({
      key: 'overruledBy',
      title: i18n.t('inspectionTaskResult.model.attributes.overruledBy'),
      params: [displayablePropertyParam({path: 'overruledUser.fullName', transform: transformation.none})],
      template: '{value}',
      sorter: (a, b) => sortAlphabetically(a.overruledUser.fullName, b.overruledUser.fullName),
    }),
    displayableProperty({
      key: 'overruledRemark',
      title: i18n.t('inspectionTaskResult.model.attributes.overruledRemark'),
      params: [displayablePropertyParam({path: 'overruledRemark', transform: transformation.none})],
      template: '{value}',
      sorter: (a, b) => sortAlphabetically(a.overruledRemark, b.overruledRemark),
    }),
  ];

  populateAttributesFromStore(rootStore) {
    super.populateAttributesFromStore(rootStore);
    this.inspectionTask = rootStore.inspectionTaskStore.getById(this.inspectionTaskId);
    this.overruledUser = rootStore.userStore.getById(this.overruledUserId);
    this.createdByUser = rootStore.userStore.getById(this.createdBy);
  }

  static allDisplayableProperties(rootStore, pathPrefix = '', titlePrefix = '') {
    const allDisplayableProperties = super.allDisplayableProperties(rootStore, pathPrefix, titlePrefix);

    const inspectionTaskPrefix = `${titlePrefix}${i18n.t('inspectionTask.model.one')} > `;
    allDisplayableProperties.push(
      ...InspectionTask.allDisplayableProperties(rootStore, `${pathPrefix}inspectionTask.`, inspectionTaskPrefix)
    );

    return allDisplayableProperties;
  }

  get overdueDifference() {
    if (!this.dueDate) {
      return null;
    }

    return moment(this.createdAt).diff(moment(this.dueDate), 'seconds');
  }

  get overdueDifferenceAbsolute() {
    return Math.abs(this.overdueDifference);
  }

  get differenceInMinutes() {
    const diff = this.overdueDifference;
    return diff ? Math.round(diff / 60) : 0;
  }
}

decorate(InspectionTaskResult, {
  id: observable,
  inspectionTaskId: observable,
  inspectionTask: observable,
  value: observable,
  valuation: observable,
  remark: observable,
  overruledUserId: observable,
  overruledUser: observable,
  overruledValue: observable,
  overruledValuation: observable,
  overruledRemark: observable,
  dueDate: observable,
  createdBy: observable,
  createdByUser: observable,
  overdueDifference: computed,
  overdueDifferenceAbsolute: computed,
  differenceInMinutes: computed,
});

export const InspectionTaskValuation = {
  required: 'R',
  accepted: 'A',
};
